import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { InfoTip } from '../feedback/InfoTip';

interface FormFieldProps {
	name: string;
	label?: React.ReactNode;
	error?: string;
	warning?: string;
	required?: boolean;
	help?: string;
	/**
	 * Explanation shown behind an info icon beside the label, for a field whose
	 * meaning is not obvious. Prefer this to `help` on working pages: the owner
	 * rule (2026-10-10) is no on-screen help text where people work all day.
	 * Rendered outside the `<label>` because a button inside a label is invalid
	 * HTML and would steal the label's click.
	 */
	info?: React.ReactNode;
	/**
	 * The form control element. Typically a single input-like component
	 * (Input, Select, SingleSelect, ClientSelector, etc.) that FormField
	 * clones to inject `id` / `aria-*` / `size` props.
	 *
	 * Multiple children are tolerated: the first valid React element is
	 * treated as the form control and gets the cloned props; any further
	 * elements render as siblings after it (useful for inline hints /
	 * callouts that live tightly under the input). Booleans, nulls, and
	 * strings between the input and any trailing siblings are passed
	 * through unchanged.
	 */
	children: React.ReactNode;
	className?: string;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	/**
	 * Render the label for assistive tech only. For a control that already
	 * names itself — a typeahead whose placeholder reads "Search companies…"
	 * — a visible label just repeats it and costs a row of height. The label
	 * still has to EXIST: an input whose only name is a placeholder is
	 * unreadable to a screen reader, and the name vanishes once a value is
	 * entered.
	 */
	labelHidden?: boolean;
}

/**
 * The label-side info icon. Its own component so the translation hook runs
 * only when a field asks for a tip: FormField itself renders in places with
 * no TranslationProvider, where the hook would throw.
 */
function FieldInfoTip({
	label,
	content,
}: {
	label: React.ReactNode;
	content: React.ReactNode;
}): React.ReactElement {
	const { t } = useTranslation();
	// Named for the field ("About Status") so the button's accessible name
	// does not repeat the control's own label.
	return (
		<InfoTip
			content={content}
			label={typeof label === 'string' ? `${t('About')} ${label}` : undefined}
		/>
	);
}

export function FormField({
	name,
	label,
	error,
	warning,
	required,
	help,
	info,
	children,
	className,
	size = 'md',
	labelHidden = false,
}: FormFieldProps) {
	const colors = useThemeColors();
	const fieldId = `field-${name}`;
	const errorId = `${fieldId}-error`;
	const warningId = `${fieldId}-warning`;
	const helpId = `${fieldId}-help`;

	// Determine label text size based on size prop
	const labelSizeClass = {
		xs: 'text-xs',
		sm: 'text-sm',
		md: 'text-sm',
		lg: 'text-base',
	}[size];

	// Locate the first valid React element to treat as the form control —
	// the rest render as siblings. Without this normalization, callers that
	// pass `<Input/>` + a conditional inline hint (e.g. DiscrepancyHint on
	// the license edit panel) hand us a children *array*, and
	// `React.cloneElement(array, …)` returns `{type: undefined, …}` which
	// blows up at render with "Element type is invalid: got: undefined".
	const childArray = React.Children.toArray(children);
	const firstElementIdx = childArray.findIndex(React.isValidElement);
	const formControl =
		firstElementIdx >= 0
			? // findIndex guarantees the element exists and is a valid element;
				// the props type is opaque here, so type it as a string-keyed bag
				// for the cloneElement prop injection below.
				(childArray[firstElementIdx] as React.ReactElement<
					Record<string, unknown>
				>)
			: null;
	const trailingChildren =
		firstElementIdx >= 0 ? childArray.slice(firstElementIdx + 1) : [];

	const childWithProps =
		formControl != null
			? React.cloneElement(formControl, {
					id: fieldId,
					'aria-invalid': !!error,
					'aria-describedby':
						[error && errorId, warning && warningId, help && helpId]
							.filter(Boolean)
							.join(' ') || undefined,
					'aria-required': required,
					size: size,
				})
			: null;

	return (
		<div className={cn('space-y-1', className)}>
			{label && (
				<div className={cn(info != null && !labelHidden && 'flex items-center gap-1')}>
					<label
						htmlFor={fieldId}
						className={cn(
							'block font-medium',
							labelHidden ? 'sr-only' : labelSizeClass,
							!labelHidden && colors.classes.textPrimary,
						)}
					>
						{label}
						{required === true && (
							<span
								className={cn(colors.classes.textDanger, 'ml-1')}
								aria-label="required"
							>
								*
							</span>
						)}
					</label>
					{info != null && !labelHidden && (
						<FieldInfoTip label={label} content={info} />
					)}
				</div>
			)}

			{childWithProps}

			{trailingChildren}

			{error && (
				<p
					id={errorId}
					className={cn(labelSizeClass, colors.classes.textDanger)}
					role="alert"
				>
					{error}
				</p>
			)}

			{warning && !error && (
				<p
					id={warningId}
					className={cn(labelSizeClass, colors.classes.textWarning)}
				>
					{warning}
				</p>
			)}

			{help && !error && !warning && (
				<p
					id={helpId}
					className={cn(labelSizeClass, colors.classes.textSecondary)}
				>
					{help}
				</p>
			)}
		</div>
	);
}
